<template>
    <el-checkbox
        :value="value"
        :checked="checked"
        :disabled="disabled"
        :indeterminate="indeterminate"
        :border="border"
        :label="label"
        :true-label="trueLabel"
        :false-label="falseLabel"
        :name="name"
        :size="size"
        :data-test="dataTestName"
        :data-testid="dataTestName || 'input-checkbox'"
        v-bind="$attrs"
        class="base-checkbox"
        v-on="listeners"
        @change="onChange"
    >
        <slot />
    </el-checkbox>
</template>

<script setup lang="ts">
import { Checkbox as ElCheckbox } from 'element-ui'
import { defineComponent, useListeners } from 'vue'

const listeners = useListeners()

defineComponent({
    inheritAttrs: false
})

type TModel = string | number | boolean

interface IProps {
    value?: TModel
    label?: TModel
    trueLabel?: string | number
    falseLabel?: string | number
    disabled?: boolean
    border?: boolean
    checked?: boolean
    indeterminate?: boolean
    handUpdate?: boolean
    name?: string
    size?: 'medium' | 'small' | 'mini'
    dataTestName?: string
}

const props = withDefaults(defineProps<IProps>(), {
    value: undefined,
    label: undefined,
    trueLabel: undefined,
    falseLabel: undefined,
    disabled: false,
    border: false,
    checked: undefined,
    indeterminate: false,
    handUpdate: false,
    name: undefined,
    size: 'small',
    dataTestName: 'base-checkbox'
})

const emit = defineEmits<{
    (e: 'input', pl: TModel): void
    (e: 'change', pl: TModel): void
    (e: 'update:checked', pl?: TModel): void
}>()

/* Methods */
function onChange (val: TModel) {
    if (!props.handUpdate) {
        emit('input', val)
        emit('change', val)
        emit('update:checked', val)
    } else {
        emit('update:checked', !props.value)
    }
}
</script>

<style lang="scss">
.base-checkbox, .el-checkbox.base-checkbox {
    @apply inline-flex items-center;
    .el-checkbox__input {
        @apply flex items-center justify-center;
    }

    &.is-bordered:not(.is-disabled).is-checked {
        @apply border-active-elements;
    }

    &.is-disabled {
        @apply opacity-50 border-inactive-elements;
    }
}
</style>
